// Doc 14 (SMS-7) — per-(user, channel) consent read shape.
//
// This is the SMS-a spine's read contract: what the portal `/me` SMS card
// (SMS-e) renders and what the opt-in / withdraw endpoints return. The consent
// lifecycle itself (request → confirm → withdraw → phone-change reset) is the
// pure logic in apps/backend/src/sms/channel-consent.ts; this file is just the
// over-the-wire shape.
//
// The consented phone is returned MASKED (last 4) — the raw E.164 is never sent
// to the browser (mirrors how banking last-4 is surfaced). `state` is the
// derived consent state so the client doesn't re-implement the state machine.
import { z } from 'zod';
import { NotificationChannelSchema } from './notification.schema.js';
/** Why a consent row was withdrawn (SMS-4 / SMS-5). */
export const ChannelConsentWithdrawnReasonSchema = z.enum([
    'stop_keyword', // inbound STOP/UNSUBSCRIBE (SMS-4)
    'portal_toggle_off', // user turned the toggle off (SMS-5)
    'phone_changed', // phone-of-record changed → consent reset (SMS-5)
]);
/** Derived consent state — mirrors deriveConsentState() in the backend. */
export const ChannelConsentStateSchema = z.enum([
    'none', // no row / never requested → channel is off
    'requested', // double opt-in pending (confirm SMS sent, handset hasn't replied YES)
    'confirmed', // active consent → channel may send
    'withdrawn', // STOP or toggled off → channel is off until re-opt-in
]);
/** The read shape the portal consumes. */
export const NotificationChannelPreferenceShape = z.object({
    channel: NotificationChannelSchema,
    state: ChannelConsentStateSchema,
    /** Last 4 digits of the consented E.164 number, or null when none on record. */
    consentedPhoneLast4: z.string().length(4).nullable(),
    consentRequestedAt: z.string().datetime().nullable(),
    consentConfirmedAt: z.string().datetime().nullable(),
    withdrawnAt: z.string().datetime().nullable(),
    withdrawnReason: ChannelConsentWithdrawnReasonSchema.nullable(),
    consentCopyVersion: z.string().nullable(),
});
/** `/me/<persona>/sms` response — the consent state plus whether the SMS channel
 *  is switched on for this environment (`SMS_ENABLED`). The portal hides the
 *  opt-in card when it's off, so production can carry the SMS code dark without
 *  offering a sign-up that can't send. */
export const SmsPreferenceResponseShape = NotificationChannelPreferenceShape.extend({
    smsEnabled: z.boolean(),
});
/** Portal opt-in request (SMS-e) — the raw phone the user wants SMS at; the
 *  server normalizes it to E.164 and rejects an unparseable number. */
export const SmsOptInRequestShape = z.object({
    phone: z.string().min(1, 'Phone is required'),
});
/** Portal opt-in result — the new preference state + whether the double-opt-in
 *  confirmation SMS actually went out (false when SMS is dark/unconfigured, so the
 *  UI can say "SMS isn't enabled yet" rather than "check your phone"). */
export const SmsOptInResultShape = SmsPreferenceResponseShape.extend({
    confirmationSent: z.boolean(),
});
//# sourceMappingURL=channel-preference.schema.js.map